import { tzOffset } from '../../helpers/date-time';
import { GridTemplateType } from '../../helpers/mappers';
import { buildParams } from '../../helpers/path';
import { CustomCacheRequest, DefaultRequest, WithArchiveRequest } from '../../types/props';
import { CheckInType } from './check-in-check-out-api';
import { EmployeeType } from './employees-api';
import { managerApiUrl } from './manager-api';
import { baseFetch } from './utils.ts/base-fetch';
import { fileFetch, downloadFile } from './utils.ts/file-fetch';

export type ShiftType = {
  id: string;
  staff: Pick<EmployeeType, 'id' | 'name' | 'surname' | 'tags' | 'places'>;
  start_time?: string;
  end_time?: string;
  checkin?: CheckInType;
  is_day_off?: boolean;
};

const baseUrl = `${managerApiUrl}/shift`;

export type PostShiftRequest = {
  staff_ids: string[];
  times: { start_time: string; end_time: string }[];
};

export const postShift = async (
  request: PostShiftRequest,
): Promise<{ created_shifts: ShiftType[] }> => {
  const response = await baseFetch(baseUrl, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response;
};

export type FetchShiftListRequest = {
  place_ids: string[];
  tag_ids: string[];
  start_time: string;
  end_time: string;
} & CustomCacheRequest &
  DefaultRequest &
  WithArchiveRequest;

export type ShiftsUploadType = {
  shifts: {
    staff: string;
    start_time: string;
    end_time: string;
    tag: string;
  }[];
  upload_token: string;
};

export const fetchShiftList = async (
  request: Partial<FetchShiftListRequest>,
): Promise<ShiftType[]> => {
  const { customCache, ...other } = request;
  const response = await baseFetch(baseUrl + buildParams({ ...other, offset: -tzOffset }), {
    customCache,
  });
  return response?.shifts;
};

export const fetchShift = async (request: { id: string }): Promise<ShiftType> => {
  const response = await baseFetch(baseUrl + buildParams(request));
  return response;
};

export type PutShiftRequest = {
  action: 'delete' | 'update' | 'create' | string;
} & ShiftType;

export const crudShift = async (
  request: Partial<PutShiftRequest>[],
): Promise<{
  created_shifts: ShiftType[];
  deleted_shifts: ShiftType[];
  updated_shifts: ShiftType[];
}> => {
  const response = await baseFetch(baseUrl, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
  return response;
};

export const restoreShift = async (id: string): Promise<void> => {
  const response = await baseFetch(`${baseUrl}/${id}/restore`, { method: 'POST' });
  return response;
};

export const deleteShift = async (id: string): Promise<void> => {
  const response = await baseFetch(`${baseUrl}/${id}`, { method: 'DELETE' });
  return response;
};

export const uploadShifts = async (body: FormData): Promise<ShiftsUploadType> => {
  body.append('offset', (-tzOffset).toString());
  const response = await baseFetch(`${managerApiUrl}/staff/upload`, {
    method: 'POST',
    body,
  });

  return response;
};

export const confirmUpload = async (token: string): Promise<void> => {
  const response = await baseFetch(`${managerApiUrl}/staff/confirm-upload`, {
    method: 'POST',
    body: JSON.stringify({ upload_token: token }),
  });
  return response;
};

export const shiftsTemplateDownload = async (type: GridTemplateType) => {
  const resp = await fileFetch(baseUrl + '/template' + buildParams({ type }));
  downloadFile(resp.blob, resp.fileName || 'shifts template.' + type);
};
